/* eslint-disable eslint-comments/disable-enable-pair */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import {
	Button,
	Checkbox,
	Divider,
	Form,
	Input,
	Modal,
	Select,
	Space,
	Spin,
	Tag,
	message,
} from 'antd';
import {
	ExclamationCircleFilled,
	PlusOutlined,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import {
	deleteNotificationSettings,
	getNotificationIntervals,
	getNotificationSettings,
	updateNotificationSettings,
} from '../../redux/notificationSettingsSlice';

const NotificationSettings = () => {
	const [form] = Form.useForm();
	const dispatch = useDispatch();
	const [makingApiCall, setMakingApiCall] = useState(false);
	const [customEmailInput, setCustomEmailInput] = useState('');
	const [customEmails, setCustomEmails] = useState([]);

	const { intervals } = useSelector((state) => state.notificationSettings);
	const { Brand } = useSelector((state) => state.brand);
	const primaryColor = Brand?.brandData?.webPrimary || '#faa200';

	const fetchAll = async () => {
		setMakingApiCall(true);
		try {
			await Promise.all([
				dispatch(getNotificationIntervals()).unwrap(),
				dispatch(getNotificationSettings()).unwrap().then((data) => {
					if (data && Object.keys(data).length > 0) {
						form.setFieldsValue({
							notificationIntervals: data.notificationIntervals || [],
							notifySuperAdmins: data.notifySuperAdmins || false,
							notifyFleetOwners: data.notifyFleetOwners || false,
							notifyDrivers: data.notifyDrivers || false,
						});
						setCustomEmails(data.customEmails || []);
					}
				}).catch(() => {
					// No settings configured yet — leave form at defaults
				}),
			]);
		} catch {
			message.error('Failed to load notification settings.');
		} finally {
			setMakingApiCall(false);
		}
	};

	useEffect(() => {
		fetchAll();
	}, []);

	const handleAddEmail = () => {
		const email = customEmailInput.trim().toLowerCase();
		if (!email) return;
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		if (!emailRegex.test(email)) {
			message.error('Please enter a valid email address.');
			return;
		}
		if (customEmails.includes(email)) {
			message.warning('This email is already in the list.');
			return;
		}
		setCustomEmails([...customEmails, email]);
		setCustomEmailInput('');
	};

	const handleRemoveEmail = (email) => {
		setCustomEmails(customEmails.filter((e) => e !== email));
	};

	const handleSave = async () => {
		try {
			const values = await form.validateFields();
			setMakingApiCall(true);
			const payload = {
				notificationIntervals: values.notificationIntervals || [],
				notifySuperAdmins: values.notifySuperAdmins || false,
				notifyFleetOwners: values.notifyFleetOwners || false,
				notifyDrivers: values.notifyDrivers || false,
				customEmails,
			};
			await dispatch(updateNotificationSettings(payload)).unwrap();
			message.success('Notification settings saved successfully.');
		} catch (err) {
			if (err?.errorFields) return; // form validation error
			message.error('Failed to save notification settings.');
		} finally {
			setMakingApiCall(false);
		}
	};

	const handleDisable = async () => {
		setMakingApiCall(true);
		try {
			await dispatch(deleteNotificationSettings()).unwrap();
			form.resetFields();
			setCustomEmails([]);
			message.success('Notifications disabled.');
		} catch {
			message.error('Failed to disable notifications.');
		} finally {
			setMakingApiCall(false);
		}
	};

	const showResetConfirm = () => {
		Modal.confirm({
			title: 'Are you sure you want to continue?',
			content: 'This will clear all notification settings, and no emails will be sent.',
			icon: <ExclamationCircleFilled style={{ color: primaryColor }} />,
			autoFocusButton: null,
			okText: 'Continue',
			cancelText: 'Cancel',
			cancelButtonProps: {
				style: { color: 'black', backgroundColor: 'white' },
			},
			okButtonProps: {
				style: { color: 'white', backgroundColor: primaryColor },
			},
			onOk() {
				handleDisable();
			},
			onCancel() {},
		});
	};

	return (
		<Spin spinning={makingApiCall}>
			<Form form={form} layout='vertical' style={{ maxWidth: 600 }}>
				<Divider orientation='left'>Notification Intervals</Divider>
				<Form.Item
					name='notificationIntervals'
					label='Send notification before expiry'
					rules={[
						{
							required: true,
							message: 'Please select at least one interval.',
							type: 'array',
							min: 1,
						},
					]}>
					<Select
						mode='multiple'
						placeholder='Select intervals...'
						loading={intervals.loading}
						optionFilterProp='label'
						options={(intervals.data || []).map((interval) => ({
							value: interval.value,
							label: interval.label,
						}))}
					/>
				</Form.Item>

				<Divider orientation='left'>Who Should Be Notified</Divider>

				<Form.Item style={{ marginBottom: 8 }}>
					<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
						<span>
							<strong>Super Admins</strong>
							<span style={{ color: '#888', marginLeft: 8 }}>
								(receive notifications for all drivers)
							</span>
						</span>
						<Form.Item name='notifySuperAdmins' valuePropName='checked' noStyle>
							<Checkbox className='dark-border-checkbox' />
						</Form.Item>
					</div>
				</Form.Item>

				<Form.Item style={{ marginBottom: 8 }}>
					<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
						<span>
							<strong>Fleet Operators</strong>
							<span style={{ color: '#888', marginLeft: 8 }}>
								(receive notifications only for their fleet)
							</span>
						</span>
						<Form.Item name='notifyFleetOwners' valuePropName='checked' noStyle>
							<Checkbox className='dark-border-checkbox' />
						</Form.Item>
					</div>
				</Form.Item>

				<Form.Item style={{ marginBottom: 8 }}>
					<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
						<span>
							<strong>Drivers</strong>
							<span style={{ color: '#888', marginLeft: 8 }}>
								(receive notifications only for their own documents)
							</span>
						</span>
						<Form.Item name='notifyDrivers' valuePropName='checked' noStyle>
							<Checkbox className='dark-border-checkbox' />
						</Form.Item>
					</div>
				</Form.Item>

				<Divider orientation='left'>Custom Emails</Divider>

				<Form.Item label='Add custom email recipients'>
					<Space.Compact style={{ width: '100%' }}>
						<Input
							placeholder='e.g. admin@company.com'
							value={customEmailInput}
							onChange={(e) => setCustomEmailInput(e.target.value)}
							onPressEnter={handleAddEmail}
						/>
						<Button icon={<PlusOutlined />} onClick={handleAddEmail}>
							Add
						</Button>
					</Space.Compact>
				</Form.Item>

				{customEmails.length > 0 && (
					<Form.Item>
						<div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
							{customEmails.map((email) => (
								<Tag
									key={email}
									closable
									onClose={() => handleRemoveEmail(email)}
									style={{ fontSize: 13, padding: '2px 8px' }}>
									{email}
								</Tag>
							))}
						</div>
					</Form.Item>
				)}

				<Divider />

				<Form.Item>
					<Space>
                        <Button
							style={{ color: 'black', backgroundColor: 'white'}}
							onClick={showResetConfirm}
							loading={makingApiCall}>
							Reset
						</Button>
						<Button
							style={{ color: 'white', backgroundColor: primaryColor, borderColor: primaryColor }}
							onClick={handleSave}
							loading={makingApiCall}>
							Save
						</Button>
					</Space>
				</Form.Item>
			</Form>
		</Spin>
	);
};

export default NotificationSettings;
